// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo } from 'react';

import type { EventsPerUserResp } from '@evolvemep/api-ts';
import type { AgChartOptions, AgSeriesTooltipRendererParams } from 'ag-charts-enterprise';

import { ChartRenderer } from 'modules/Shop/Insights/RevitUsage/components/ChartRenderer';
import type { AnalyticsChartProps } from 'modules/Shop/Insights/RevitUsage/types';

import { useAnalyticsChart } from './useAnalyticsChart';

export const EventsPerUserChart = ({ showDemo, ...props }: AnalyticsChartProps<EventsPerUserResp>) => {
  const [data, loading] = useAnalyticsChart(`usage-stats/v1/reporting/summary/events`, props);

  const options = useMemo<AgChartOptions>(
    () => ({
      data: data?.eventsPerUser
        .toSorted((a, b) => b.totalEvents - a.totalEvents)
        .map((u) => ({
          ...u,
          name: u.email.split('@')[0],
        })),
      title: {
        enabled: !showDemo,
        text: 'Events per User',
        textAlign: 'left',
      },
      series: [
        {
          type: 'bar',
          xKey: 'name',
          yKey: 'totalEvents',
          yName: 'Total Events',
          fill: '#30c2bf',
          tooltip: {
            renderer: ({ datum }: AgSeriesTooltipRendererParams<EventsPerUserResp['eventsPerUser'][number]>) => ({
              heading: datum.email,
              data: [
                {
                  label: 'Total Events',
                  value: String(datum.totalEvents),
                },
              ],
            }),
          },
        },
      ],
      axes: {
        x: {
          type: 'category',
          position: 'bottom',
          label: {
            rotation: -35,
            minSpacing: 0,
          },
        },
        y: {
          type: 'number',
          position: 'left',
          label: {},
        },
      },
    }),
    [data, showDemo],
  );

  return <ChartRenderer options={options} loading={loading} />;
};
